import moment from 'moment';
import { EventTypes } from 'redux-segment';

import fetchAppointment from 'actions/appointment/fetchAppointment.action';
import removeAppointment from 'actions/appointment/removeAppointment.action';
import updateAppointmentProcedures from 'actions/appointment/updateAppointmentProcedures.action';
import { createDepositBookingVersion } from 'actions/payment/beCreateDeposit.action';
import { GET_CATEGORIES_FAILURE, GET_CATEGORIES_REQUEST, GET_CATEGORIES_SUCCESS } from 'actions/shared.types';
import CONF from 'config/conf.singleton';
import STORE from 'config/store.singleton';
import { AUTH_API } from 'middleware/api';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import asArray from 'utils/arrays/asArray.util';
import { lastOf } from 'utils/arrays/elements.util';
import logger from 'utils/logger.util';
import tracked from 'utils/redux/tracked.util';

import {
  ADD_SERVICE_BOOKING,
  BOOKING_NEW_CLIENT_FAILURE,
  BOOKING_NEW_CLIENT_REQUEST,
  BOOKING_PICK_CLIENT,
  BOOKING_UPDATE_CLIENT_FAILURE,
  BOOKING_UPDATE_CLIENT_LIST_SUCCESS,
  BOOKING_UPDATE_CLIENT_REQUEST,
  CHANGE_APPOINTMENT_STATUS_FAILURE,
  CHANGE_APPOINTMENT_STATUS_REQUEST,
  CHANGE_APPOINTMENT_STATUS_SUCCESS,
  CHANGE_BOOKING_NOTES,
  CHANGE_REPEAT_FREQ,
  CHANGE_REPEAT_FREQTYPE,
  CHANGE_REPEAT_STATUS,
  CHANGE_REPEAT_UNTIL,
  CLEAN_NEWBOOKING_DATA,
  CLEAR_CHARGE_ERROR,
  CLEAR_DELETE_APPOINTMENT_INFO,
  CLEAR_FUTURE_BOOKING_DATA,
  CLIENT_LAST_VISIT_FAILURE,
  CLIENT_LAST_VISIT_REQUEST,
  CLIENT_LAST_VISIT_SUCCESS,
  CLIENT_LIST_FAILURE,
  CLIENT_LIST_REQUEST,
  CLIENT_LIST_SUCCESS,
  CLIENT_NO_SHOW_RATE_FAILURE,
  CLIENT_NO_SHOW_RATE_REQUEST,
  CLIENT_NO_SHOW_RATE_SUCCESS,
  CREATE_APPOINTMENT_WITH_SERVICES_FAILURE,
  CREATE_APPOINTMENT_WITH_SERVICES_REQUEST,
  CREATE_APPOINTMENT_WITH_SERVICES_SUCCESS,
  CREATE_DEPOSIT_BACK_END_FAILURE,
  CREATE_DEPOSIT_IN_CHECKOUT,
  CS_TEAM_FAILURE,
  CS_TEAM_REQUEST,
  CS_TEAM_SUCCESS,
  EDIT_SERVICE,
  EDIT_SERVICE_CLIENT,
  EDIT_SERVICE_DURATION,
  EDIT_SERVICE_PRICE,
  EDIT_SERVICE_PROCESSING,
  FETCH_APPOINTMENT_STARTTIME_FAILURE,
  FETCH_APPOINTMENT_STARTTIME_REQUEST,
  FETCH_APPOINTMENT_STARTTIME_SUCCESS,
  FETCH_CLIENT_FAILURE,
  FETCH_CLIENT_REQUEST,
  FETCH_CLIENT_SUCCESS,
  FETCH_EVENT_FOR_EDIT_FAILURE,
  FETCH_EVENT_FOR_EDIT_REQUEST,
  FETCH_EVENT_FOR_EDIT_SUCCESS,
  FREQUENTLY_LIST_FAILURE,
  FREQUENTLY_LIST_REQUEST,
  FREQUENTLY_LIST_SUCCESS,
  GET_CLIENT_DEPOSIT_DETAILS_FAILURE,
  GET_CLIENT_DEPOSIT_DETAILS_REQUEST,
  GET_CLIENT_DEPOSIT_DETAILS_SUCCESS,
  GET_PREFERENCE_FAILURE,
  GET_PREFERENCE_REQUEST,
  GET_PREFERENCE_SUCCESS,
  PICK_CATEGORY,
  REMOVE_ADDED_SERVICE,
  REMOVE_NEW_APPOINTMENT,
  SET_ALL_DAY,
  SET_END_TIME,
  SET_IS_EDIT_BOOKING,
  SET_REPEAT_FOREVER,
  SET_REPEAT_RULE,
  SET_TYPE,
  SHOW_MORE_REPEAT_FAILURE,
  SHOW_MORE_REPEAT_REQUEST,
  SHOW_MORE_REPEAT_SUCCESS,
  UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_FAILURE,
  UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_REQUEST,
  UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_SUCCESS,
  UPDATE_APPOINTMENT_WITH_SERVICES_SUCCESS,
  UPDATE_CLIENT_DATA,
  UPDATE_NEWBOOKING_DATE,
  UPDATE_SERVICE_BOOKING,
} from 'booking/constants';

const L = logger('booking/actions');

export const changeRepeat = () => ({ type: CHANGE_REPEAT_STATUS });
export const changeRepeatFrequency = (frequency) => ({ type: CHANGE_REPEAT_FREQ, frequency });
export const changeRepeatFrequencyType = (frequencyType) => ({ type: CHANGE_REPEAT_FREQTYPE, frequencyType });
export const changeRepeatUntil = (until) => ({ type: CHANGE_REPEAT_UNTIL, until });
export const appointmentAndRepeatThunk = (id, data) => {
  // thunk here for adding deposit
  const rules = { ...data, frequency_type: 'weeks' };
  const url = `salon/${id}/repeating/appointment/`;
  return {
    [AUTH_API]: {
      api_root: CONF.apiRoot,
      endpoint: url,
      authenticated: true,
      types: [CREATE_APPOINTMENT_WITH_SERVICES_REQUEST, CREATE_APPOINTMENT_WITH_SERVICES_SUCCESS, CREATE_APPOINTMENT_WITH_SERVICES_FAILURE],
      params: { rules, method: 'POST' },
      config: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(rules),
      },
    },
  };
};

export const updateAppointmentAndRepeatThunk = (salonId, procedures, rules) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/${salonId}/repeating/appointment/${procedures[0].appointment.id}/`,
    authenticated: true,
    types: [UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_REQUEST, UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_SUCCESS, UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_FAILURE],
    params: { salonId, procedures },
    config: {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: procedures[0].appointment.client ? procedures[0].appointment.client.id : null,
        procedures,
        frequency_type: 'weeks',
        ...rules,
      }),
    },
  },
});

export const setAllDay = () => ({ type: SET_ALL_DAY });
export const setIsEditBooking = (isEditBooking) => ({ type: SET_IS_EDIT_BOOKING, isEditBooking });
export const setRepeatingRule = (repeatRule) => ({ type: SET_REPEAT_RULE, repeat_rule: repeatRule });
export const setRepeatForever = (forever, endDate) => ({ type: SET_REPEAT_FOREVER, forever, endDate });

export const pickCategory = (category) => ({ type: PICK_CATEGORY, category });
export const setType = (bookingType) => ({ type: SET_TYPE, bookingType });
export const setEndTime = (endTime) => ({ type: SET_END_TIME, endTime });

export const fetchClientDepositDetails = (salonId, clientId) => ({
  [AUTH_API]: {
    api_root: CONF.transactionApiRoot,
    endpoint: `deposit/${salonId}/client/${clientId}/`,
    authenticated: true,
    types: [GET_CLIENT_DEPOSIT_DETAILS_REQUEST, GET_CLIENT_DEPOSIT_DETAILS_SUCCESS, GET_CLIENT_DEPOSIT_DETAILS_FAILURE],
  },
});

export const changeService = (service, id) => ({
  type: EDIT_SERVICE,
  service,
  id,
  meta: {
    analytics: EventTypes.track,
  },
});
export const changeClient = (client) => ({
  type: EDIT_SERVICE_CLIENT,
  client,
  meta: {
    analytics: EventTypes.track,
  },
});
export const removeService = (id, local) => ({ type: REMOVE_ADDED_SERVICE, id, local });

export const changeDuration = ({ duration, id, custom }) => ({
  type: EDIT_SERVICE_DURATION,
  duration: duration.toISOString(),
  id,
  custom,
  meta: { analytics: EventTypes.track },
});

export const changeProcessing = ({ processing, id, custom }) => ({
  type: EDIT_SERVICE_PROCESSING,
  processing: processing.toISOString(),
  id,
  custom,
  meta: { analytics: EventTypes.track },
});

export const editPrice = ({ price, id, custom }) => ({
  type: EDIT_SERVICE_PRICE,
  price,
  id,
  custom,
  meta: { analytics: EventTypes.track },
});

export const changeBookingNotes = (notes) => ({ type: CHANGE_BOOKING_NOTES, notes });

// TODO: replace all 3 versions of this action with one new beGetServices
export const getCategories = (salonId) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/v2/${salonId}/category/services/`,
    authenticated: true,
    types: [GET_CATEGORIES_REQUEST, GET_CATEGORIES_SUCCESS, GET_CATEGORIES_FAILURE],
  },
});

export const saveClientEdit = (data) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/${data.salon_id}/client/${data.id}/`,
    authenticated: true,
    types: [BOOKING_UPDATE_CLIENT_REQUEST, UPDATE_CLIENT_DATA, BOOKING_UPDATE_CLIENT_FAILURE],
    params: { data, method: 'PUT' },
    config: { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
    meta: { analytics: EventTypes.track },
  },
});

export function getClientLastVisit(salonId, clientId) {
  return {
    [AUTH_API]: {
      api_root: CONF.apiRoot,
      endpoint: `salon/v2/${salonId}/client/${clientId}/lastVisit/`,
      authenticated: true,
      types: [CLIENT_LAST_VISIT_REQUEST, CLIENT_LAST_VISIT_SUCCESS, CLIENT_LAST_VISIT_FAILURE],
      params: { clientId, method: 'GET' },
      config: {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    },
  };
}

export function getClient(salonId, clientId) {
  // fetch the client to a unique new_booking reducer data to avoid
  // clashing with other async requests
  return {
    [AUTH_API]: {
      api_root: CONF.apiRoot,
      endpoint: `salon/${salonId}/client/${clientId}/`,
      authenticated: true,
      types: [FETCH_CLIENT_REQUEST, FETCH_CLIENT_SUCCESS, FETCH_CLIENT_FAILURE],
      params: { clientId, method: 'GET' },
      config: {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    },
  };
}

export const createAppointmentWithProcedures = L.wrap('createAppointmentWithProcedures()', (salonId, data, deposit) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/${salonId}/appointment/`,
    authenticated: true,
    types: [CREATE_APPOINTMENT_WITH_SERVICES_REQUEST, CREATE_APPOINTMENT_WITH_SERVICES_SUCCESS, CREATE_APPOINTMENT_WITH_SERVICES_FAILURE],
    params: { data, method: 'POST', deposit },
    config: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
  },
}));

export const removeNewAppointment = () => ({ type: REMOVE_NEW_APPOINTMENT });
export const createDepositFrontEnd = (deposit) => ({ type: CREATE_DEPOSIT_IN_CHECKOUT, deposit });

export const appointmentAndDepositThunk = (salonId, data, deposit) => (dispatch) => {
  dispatch(createAppointmentWithProcedures(salonId, data, deposit)).then((res) => {
    if (res.type === CREATE_APPOINTMENT_WITH_SERVICES_SUCCESS) {
      dispatch(
        createDepositBookingVersion(
          {
            clientId: deposit.clientId,
            amount: deposit.amount,
            depositType: deposit.depositType,
            notes: 'notes not yet enabled...',
            created_for_appointmentId: res.response.id,
            source: deposit.source,
          },
          salonId,
        ),
      );
    }
    return { type: CREATE_DEPOSIT_BACK_END_FAILURE };
  });
};

export const updateAppointmentWithProcedures = (salonId, appointmentId, data) => {
  // FIXME: shouldn't access store here, it should be done at the place of updateAppointmentWithProcedures invocation
  const appointmentDetails = getAnyOrVoid('scheduler.appointmentDetails', STORE.getState());
  return updateAppointmentProcedures({ salonId, appointmentId, data, appointmentDetails });
};

export function updateAppointmentAndDepositThunk(salonId, appointmentId, data, deposit) {
  return (dispatch) => {
    dispatch(updateAppointmentWithProcedures(salonId, appointmentId, data)).then((res) => {
      if (res.type === UPDATE_APPOINTMENT_WITH_SERVICES_SUCCESS) {
        dispatch(
          createDepositBookingVersion(
            {
              clientId: deposit.clientId,
              depositType: deposit.depositType,
              amount: deposit.amount,
              notes: 'notes not yet enabled...',
              created_for_appointmentId: appointmentId,
              source: deposit.source,
            },
            salonId,
          ),
        );
      }
      return { type: CREATE_DEPOSIT_BACK_END_FAILURE };
    });
  };
}

export const deleteAppointment = (salonId, appointmentId, depositData = {}, depositAction) =>
  removeAppointment({
    salonId,
    appointmentId,
    depositData,
    depositAction,
  });

export const clearDeleteAppointmentInfo = () => ({ type: CLEAR_DELETE_APPOINTMENT_INFO });

export const getAppointment = (salonId, appointmentId) => fetchAppointment({ salonId, appointmentId });

export const getAppointmentStartTimeList = (salonId, ids) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/${salonId}/appointment/start-time`,
    authenticated: true,
    types: [FETCH_APPOINTMENT_STARTTIME_REQUEST, FETCH_APPOINTMENT_STARTTIME_SUCCESS, FETCH_APPOINTMENT_STARTTIME_FAILURE],
    config: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ids),
    },
  },
});

export function getEvent(salonId, id) {
  return {
    [AUTH_API]: {
      api_root: CONF.apiRoot,
      endpoint: `salon/${salonId}/booking/${id}/`,
      authenticated: true,
      types: [FETCH_EVENT_FOR_EDIT_REQUEST, FETCH_EVENT_FOR_EDIT_SUCCESS, FETCH_EVENT_FOR_EDIT_FAILURE],
    },
  };
}

export const changeAppointmentsStatus = (salonId, appointments) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/${salonId}/appointment/changeStatus/`,
    authenticated: true,
    types: [CHANGE_APPOINTMENT_STATUS_REQUEST, CHANGE_APPOINTMENT_STATUS_SUCCESS, CHANGE_APPOINTMENT_STATUS_FAILURE],
    params: { method: 'PUT', appointments },
    config: { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(appointments) },
  },
});

export const addService = (newService) => {
  const service = { ...(newService ?? void 1) };

  return (dispatch, getState) => {
    const { services: categoryServices, stylistList: stylists } = getState().currentSalon ?? {};

    if (!service.local_id) {
      const unsavedServices = asArray(getState().booking.addedServices).filter((s) => s.local_id);

      const localId = unsavedServices.length > 0 ? lastOf(unsavedServices).local_id - 1 : -2;

      service.local_id = localId;
      service.id = localId;
    }

    return dispatch(tracked({ type: ADD_SERVICE_BOOKING, service, stylists, categoryServices }));
  };
};

export const updateService = (service) => (dispatch, getState) => {
  const state = getState();
  dispatch({
    type: UPDATE_SERVICE_BOOKING,
    service,
    stylists: state.currentSalon.stylistList,
    categoryServices: state.currentSalon.services,
    meta: { analytics: EventTypes.track },
  });
};

export const pickClient = (client) => ({ type: BOOKING_PICK_CLIENT, client, meta: { analytics: EventTypes.track } });

// FIXME: remove since it may be unused duplicate of updateNewBookingDate in src/scheduler/actions.js
export const updateNewBookingDate = (date) => ({
  type: UPDATE_NEWBOOKING_DATE,
  date,
  meta: { analytics: EventTypes.track },
});

export const createNewClient = (data) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/${data.salon_id}/client/`,
    authenticated: true,
    types: [BOOKING_NEW_CLIENT_REQUEST, BOOKING_UPDATE_CLIENT_LIST_SUCCESS, BOOKING_NEW_CLIENT_FAILURE],
    params: { data, method: 'POST' },
    config: { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
    meta: { analytics: EventTypes.track },
  },
});

// FIXME: remove if unused
export const getCSteamSupport = () => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: 'authentication/user/type/',
    authenticated: true,
    types: [CS_TEAM_REQUEST, CS_TEAM_SUCCESS, CS_TEAM_FAILURE],
    params: { method: 'GET' },
    config: { method: 'GET', headers: { 'Content-Type': 'application/json' } },
  },
});

export const getClientsList = (id) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/v2/${id}/client/list/`,
    authenticated: true,
    types: [CLIENT_LIST_REQUEST, CLIENT_LIST_SUCCESS, CLIENT_LIST_FAILURE],
    params: { method: 'GET' },
    config: {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  },
});

export const getFrequentlyBookedList = (clientId, salonId) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/v2/${salonId}/client/${clientId}/service/`,
    authenticated: true,
    types: [FREQUENTLY_LIST_REQUEST, FREQUENTLY_LIST_SUCCESS, FREQUENTLY_LIST_FAILURE],
    params: { method: 'GET' },
    config: {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  },
});

export function hasHighNoShowRate(clientId, salonId) {
  // last 12 months
  const toDate = moment().toISOString();
  const fromDate = moment().subtract(1, 'year').toISOString();
  const url = `metrics/appointment/client/noshow/${salonId}/${clientId}/${fromDate}/${toDate}/`;

  return {
    [AUTH_API]: {
      api_root: CONF.apiRoot,
      endpoint: url,
      authenticated: true,
      types: [CLIENT_NO_SHOW_RATE_REQUEST, CLIENT_NO_SHOW_RATE_SUCCESS, CLIENT_NO_SHOW_RATE_FAILURE],
      params: { method: 'GET' },
      config: {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    },
  };
}

export const getPreferenceList = (salonId, clientId) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/v2/${salonId}/client/${clientId}/preference/`,
    authenticated: true,
    types: [GET_PREFERENCE_REQUEST, GET_PREFERENCE_SUCCESS, GET_PREFERENCE_FAILURE],
    params: { method: 'GET', salonId, clientId },
    config: {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  },
});

// FIXME: remove if unused
export const showMoreRepeatBooking = (salonId, parentId, lastProcedureId) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/${salonId}/more/appointments/${parentId}/${lastProcedureId}/`,
    authenticated: true,
    types: [SHOW_MORE_REPEAT_REQUEST, SHOW_MORE_REPEAT_SUCCESS, SHOW_MORE_REPEAT_FAILURE],
    params: { method: 'GET', salonId, parentId, lastProcedureId },
    config: {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  },
});

export const cleanBookingData = () => ({ type: CLEAN_NEWBOOKING_DATA });
export const clearFutureBookingData = () => ({ type: CLEAR_FUTURE_BOOKING_DATA });
export const clearChargeError = () => ({ type: CLEAR_CHARGE_ERROR });
